import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Copy, Check } from 'lucide-react';
import { copyFromVisibleNode } from '../../utils/clipboard';
import InfoTip from './InfoTip';

/**
 * A monospace value with a copy button — safe inside dialogs, where the focus
 * trap breaks the usual textarea copy (see `copyFromVisibleNode`).
 */
const CopyableCode = ({ value, label, tooltip, ariaLabel = 'Copy to clipboard', multiline = false }) => {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopy = async () => {
    const ok = await copyFromVisibleNode(value, ref.current);
    setCopyFailed(!ok);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box>
      {label && (
        <Typography
          variant="caption"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 700, color: 'text.secondary' }}
        >
          {label}
          {tooltip && <InfoTip title={tooltip} size={13} />}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          gap: 1,
          p: 1.25,
          borderRadius: 2,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          ref={ref}
          component="code"
          sx={{
            flex: 1,
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: 12.5,
            wordBreak: multiline ? 'break-word' : 'break-all',
            // Pretty-printed JSON / multi-line commands keep their layout.
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
            maxHeight: multiline ? 320 : 'none',
            overflowY: multiline ? 'auto' : 'visible',
            userSelect: 'all',
          }}
        >
          {value}
        </Typography>
        <IconButton onClick={handleCopy} size="small" title={ariaLabel} aria-label={ariaLabel}>
          {copied ? <Check size={17} color="#10b981" /> : <Copy size={17} />}
        </IconButton>
      </Box>
      {copyFailed && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
          Couldn't access the clipboard here — it's selected, press Ctrl/⌘ + C to copy it.
        </Typography>
      )}
    </Box>
  );
};

export default CopyableCode;
